import {
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EducatorSearchResultDto, GetEducatorSearchQuery, SearchClient } from 'src/shared/services/api.service';
import { DateService } from 'src/shared/services/date.service';
import { RouteTypeService } from 'src/shared/services/route-type.service';
import { FeatureManagementService } from 'src/shared/services/feature-management.service';

@Component({
  selector: 'cert-educator-search-modal',
  templateUrl: './educator-search-modal.component.html',
  styleUrls: ['./educator-search-modal.component.scss'],
  standalone: false,
})
export class EducatorSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('form', { static: true }) ngForm!: NgForm;
  @Input() searchModalShown!: boolean;

  searching = signal(false);
  scrollSearching = signal(false);
  formChangesSubscription: any;
  educators = signal<EducatorSearchResultDto[] | undefined>(undefined);
  dateOfBirth!: Date | undefined;

  obscurityFeatureActive = signal(false);

  @Input()
  showCreateButton = false;

  @Input()
  previousSearchRequest: GetEducatorSearchQuery = {
    pageNumber: 1,
    pageSize: 10,
  };

  private cancelSearch$ = new Subject<void>();

  searchRequest: GetEducatorSearchQuery = {
    pageNumber: 1,
    pageSize: 10,
  };

  endOfResults = signal(false);

  @Output() toggleCreateButton = new EventEmitter<CreateEducatorParams>();

  constructor(
    private readonly searchClient: SearchClient,
    private readonly dateService: DateService,
    private readonly activeModalService: NgbActiveModal,
    private readonly renderer: Renderer2,
    private readonly routeTypeService: RouteTypeService,
    private readonly featureManager: FeatureManagementService,
  ) {}

  ngOnInit() {
    if (this.previousSearchRequest) {
      this.searchRequest = this.previousSearchRequest;

      if (this.searchRequest.dateOfBirth) {
        this.dateOfBirth = this.searchRequest.dateOfBirth;
      }
    }

    this.formChangesSubscription = this.ngForm.form.valueChanges.pipe(debounceTime(1000)).subscribe((input) => {
      if (input.phoneNumber === '') {
        input.phoneNumber = null;
      }

      if (input.dateOfBirth && this.dateService.isValidDate(this.dateOfBirth)) {
        this.searchRequest.dateOfBirth = new Date(input.dateOfBirth);
      }

      if (!this.isInputEmpty(input)) {
        this.getSearchResults(input);
      } else {
        this.educators.set(undefined);
      }
    });

    this.featureManager.features.subscribe((features) => {
      this.obscurityFeatureActive.set(features.informationObscurity);
    });
  }

  ngAfterViewInit() {
    this.renderer.selectRootElement('#lastNameSearch').focus();
  }

  ngOnDestroy() {
    this.formChangesSubscription.unsubscribe();
  }

  getSearchResults(searchRequest: GetEducatorSearchQuery) {
    this.endOfResults.set(false);
    this.cancelSearch$.next();

    this.searching.set(true);
    this.searchRequest.pageNumber = 1;

    if (this.dateOfBirth && this.dateService.isValidDate(this.dateOfBirth)) {
      searchRequest.dateOfBirth = this.dateOfBirth;
    } else {
      searchRequest.dateOfBirth = undefined;
    }

    this.searchClient
      .search(searchRequest)
      .pipe(takeUntil(this.cancelSearch$))
      .subscribe({
        next: (result) => {
          this.educators.set(result.educatorSearchResults);
          if (result.educatorSearchResults && result.educatorSearchResults.length < this.searchRequest.pageSize) {
            this.endOfResults.set(true);
          }
          this.toggleCreateButton.emit({ searchQuery: this.searchRequest, date: this.dateOfBirth });
        },
        complete: () => this.searching.set(false),
      });
  }

  onScroll() {
    if (!this.endOfResults()) {
      this.scrollSearching.set(true);
      this.searchRequest.pageNumber++;

      this.searchClient.search(this.searchRequest).subscribe({
        next: (result) => {
          if (result?.educatorSearchResults?.length) {
            this.endOfResults.set(false);
            this.educators.set(this.educators()?.concat(result.educatorSearchResults));

            if (result.educatorSearchResults.length < this.searchRequest.pageSize) {
              this.endOfResults.set(true);
            }
          }
        },
        complete: () => this.scrollSearching.set(false),
      });
    }
  }

  clearFormInputs() {
    this.cancelSearch$.next();

    this.searchRequest = {
      pageNumber: 1,
      pageSize: 10,
    };
    this.ngForm.reset();

    this.searching.set(false);
    this.endOfResults.set(false);
  }

  isInputEmpty(input: GetEducatorSearchQuery): boolean {
    if (this.doesMeetsSearchRequirements(input.lastName, 3)) return false;
    if (this.doesMeetsSearchRequirements(input.firstName, 2)) return false;
    if (this.doesMeetsSearchRequirements(input.middleName, 1)) return false;
    if (this.doesMeetsSearchRequirements(input.phoneNumber, 3)) return false;
    if (this.doesMeetsSearchRequirements(input.email, 3)) return false;
    if (this.doesMeetsSearchRequirements(input.ein, 7)) return false;
    if (this.doesMeetsSearchRequirements(input.ssn, 4)) return false;
    if (this.doesMeetsSearchRequirements(input.educatorStakeholderId, 4)) return false;
    if (
      input.dateOfBirth !== null &&
      input.dateOfBirth !== undefined &&
      input.dateOfBirth.toString().length > 8 &&
      !isNaN(new Date(input.dateOfBirth).getTime())
    )
      return false;

    return true;
  }

  doesMeetsSearchRequirements(input: string | undefined, length: number): boolean {
    return !input || (input && input!.length < length) ? false : true;
  }

  goToEducatorManagement(personId: number) {
    this.activeModalService.close();
    this.routeTypeService.navigateToEducatorManagement(personId);
  }

  stopPropagation(event: MouseEvent): void {
    // stop the click from propagating to the parent
    // so that we can still see the popovers while the row is clickable.
    if (event) {
      event.stopPropagation();
    }
  }
}

export interface CreateEducatorParams {
  searchQuery: GetEducatorSearchQuery;
  date: Date | undefined;
}
